import React, { JSX, useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { EmailMessage, SalesConversation, SalesConversationAdmin } from '../../../../types/entities';
import {
    ActionResult,
    Assignee,
    ConversationSubject,
    addNote,
    loadAssignees,
    loadConversation,
    loadMessages,
    markRead,
    reassign,
    sendReply,
} from './salesConversationApi';
import { notificationsChanged } from '../../../menu/notificationsApi';

/**
 * The email conversation with a contact form or enterprise request submitter.
 *
 * Shared by `ContactFormEdit` and `EnterpriseRequestEdit`, which differ only in which id they hand
 * over. Everything about a thread is the same on both sides, so there is one component rather than
 * two that drift.
 *
 * **The screen is tabbed and this panel owns a whole tab of it** (user, 2026-09-08): squeezed into
 * half an edit screen beside the enquiry it was answering, it was too much at once. With the full
 * width, the thread and the composer take the main column and the notes and handover sit beside
 * them, so replying never means scrolling past everything else.
 *
 * A record usually has no conversation: one is created by the first reply or note. That is the
 * normal empty state, not an error, and the panel is fully usable in it.
 */

type Props = {
    /** Which request this panel belongs to. */
    subject: ConversationSubject,
    /** Fallback subject line for the very first reply, before a thread exists. */
    defaultSubject: string,
};

const statusLabels: Record<string, string> = {
    open: 'Open',
    awaiting_reply: 'Awaiting their reply',
    closed: 'Closed',
};

/** en-GB, matching the date columns everywhere else in HQ. */
const formatMoment = (value: string|null|undefined): string => {
    if (typeof value !== 'string' || value === '') {
        return '';
    }
    const date: Date = new Date(value);

    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('en-GB');
};

/**
 * An admin's name, however their absence is expressed.
 *
 * API Platform leaves a null relation out of the payload rather than serialising `null`, so these
 * arrive as `undefined`. Reading `.name` off one crashed the panel through its error boundary
 * (2026-09-08), which reads as the whole feature having vanished rather than as one bad field.
 */
const nameOf = (admin: SalesConversationAdmin|null|undefined): string|null =>
    admin != null && typeof admin.name === 'string' ? admin.name : null;

/**
 * Notes written before this feature existed, off the record's own JSON column.
 *
 * Those rows have a date and a body and no author. They are left exactly as they were and shown
 * read-only rather than migrated, so nothing already written is rewritten or lost.
 */
const earlierNotes = (record: any): { date: string, note: string }[] => {
    const notes: unknown = record?.notes;
    if (!Array.isArray(notes)) {
        return [];
    }

    return notes.filter(
        (entry: any): boolean => typeof entry?.note === 'string' && entry.note.trim().length > 0
    );
};

/**
 * Who a message is from, as a person rather than a side of the conversation.
 *
 * Outbound uses the HQ user recorded against the message, not the conversation's current owner:
 * after a handover those differ, and the thread should say who actually wrote each reply. Inbound
 * uses the name off the form they submitted. Each falls back to a side when there is no name to
 * show (user, 2026-09-08).
 */
const senderLabel = (
    message: EmailMessage,
    outbound: boolean,
    theirName: string
): string => {
    if (outbound) {
        const name: string|null|undefined = message.sentByName;

        return typeof name === 'string' && name.trim().length > 0 ? name : 'NokNok';
    }

    return theirName.trim().length > 0 ? theirName.trim() : 'Them';
};

/**
 * One titled card.
 *
 * The four parts of this panel ran together as one column of headings and inputs (user,
 * 2026-09-08). An outlined card with its own header band draws the boundary the headings alone
 * were not, and takes no more vertical space than the dividers it replaces.
 */
const Section = ({ title, children }: { title: string, children: React.ReactNode }): JSX.Element => (
    <Paper variant="outlined" sx={ { overflow: 'hidden' } }>
        <Box sx={ { px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' } }>
            <Typography variant="overline" sx={ { fontWeight: 700, letterSpacing: '0.08em' } }>
                { title }
            </Typography>
        </Box>
        <Box sx={ { p: 2 } }>{ children }</Box>
    </Paper>
);

/** One handover as a sentence. */
const handoverLine = (handover: { fromUser?: any, toUser?: any, byUser?: any, reason?: string|null }): string => {
    const from: string|null = nameOf(handover.fromUser);
    const to: string = nameOf(handover.toUser) ?? 'unknown';
    const by: string|null = nameOf(handover.byUser);

    let line: string = from === null ? `claimed by ${ to }` : `${ from } to ${ to }`;
    if (by !== null && from !== null) {
        line += ` (by ${ by })`;
    }
    if (typeof handover.reason === 'string' && handover.reason.length > 0) {
        line += ` — ${ handover.reason }`;
    }

    return line;
};

export const ConversationPanel = ({ subject, defaultSubject }: Props): JSX.Element => {
    const record: any = useRecordContext();
    const notify = useNotify();

    const [conversation, setConversation] = useState<SalesConversation|null>(null);
    const [messages, setMessages] = useState<EmailMessage[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string|null>(null);
    const [busy, setBusy] = useState<boolean>(false);

    const [replySubject, setReplySubject] = useState<string>('');
    const [replyBody, setReplyBody] = useState<string>('');
    const [noteBody, setNoteBody] = useState<string>('');
    const [handTo, setHandTo] = useState<string>('');
    const [handReason, setHandReason] = useState<string>('');

    // HQ-API answers who this admin may hand a thread to, using the same rule the handover route
    // enforces, so the select cannot offer somebody that route then refuses. Owners and admins
    // reach every role; everybody else reaches their own.
    // Null until HQ-API has answered, so the select does not claim nobody is available while loading.
    const [assignable, setAssignable] = useState<Assignee[]|null>(null);
    const [assigneeError, setAssigneeError] = useState<string|null>(null);

    useEffect((): void => {
        loadAssignees()
            .then((loaded: Assignee[]): void => { setAssignable(loaded); setAssigneeError(null); })
            .catch((error: unknown): void => {
                setAssigneeError(error instanceof Error ? error.message : 'Could not load who this can be handed to.');
            });
    }, []);

    const refresh = useCallback(async (): Promise<void> => {
        setLoading(true);
        setLoadError(null);
        try {
            const loaded: SalesConversation|null = await loadConversation(subject);
            setConversation(loaded);
            setMessages(loaded === null ? [] : await loadMessages(loaded.id));
            // Opening the thread is what marks its replies read, so the header bell stops counting
            // it. Deliberately not awaited: the thread renders regardless, and a stale badge for
            // one more poll is nobody's problem.
            if (loaded !== null) {
                // Not awaited — the thread renders regardless — but the bell is told once it has
                // actually happened rather than after a guessed delay.
                void markRead(subject).then((): void => notificationsChanged());
            }
            setReplySubject((current: string): string => {
                if (current.length > 0) {
                    return current;
                }
                const base: string = loaded?.subject ?? defaultSubject;

                return base.startsWith('Re: ') ? base : `Re: ${ base }`;
            });
        } catch (error) {
            setLoadError(error instanceof Error ? error.message : 'Could not load the conversation.');
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(subject), defaultSubject]);

    useEffect((): void => {
        void refresh();
    }, [refresh]);

    /**
     * Runs one write, reports what actually happened, and reloads on success.
     *
     * Nothing is cleared until the API has confirmed it: a reply MailGun refused must leave what
     * was typed in the box rather than losing it behind an error toast.
     */
    const run = async (action: () => Promise<ActionResult>, onSuccess: () => void): Promise<void> => {
        setBusy(true);
        try {
            const result: ActionResult = await action();
            notify(result.message, { type: result.ok ? 'success' : 'error' });
            if (result.ok) {
                onSuccess();
                await refresh();
                // Replying can change what is outstanding, so the bell should not wait a minute
                // to find out.
                notificationsChanged();
            }
        } finally {
            setBusy(false);
        }
    };

    const ownerName: string|null = nameOf(conversation?.assignedTo);
    const notes = conversation?.notes ?? [];
    const handovers = conversation?.handovers ?? [];
    const earlier = earlierNotes(record);

    return (
        <Stack spacing={ 1.5 } sx={ { width: '100%' } }>

            { /* --- who has it, on every tab ------------------------------------------------ */ }
            <Stack direction="row" spacing={ 1 } alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary">{ 'Assigned to' }</Typography>
                <Chip
                    size="small"
                    label={ ownerName ?? 'Nobody yet' }
                    color={ ownerName === null ? 'default' : 'primary' }
                />
                { conversation !== null && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={ statusLabels[conversation.status] ?? conversation.status }
                    />
                ) }
                { loading && <CircularProgress size={ 14 } /> }
            </Stack>

            { loadError !== null && <Alert severity="error">{ loadError }</Alert> }

            <Grid container spacing={ 2 } sx={ { width: '100%' } }>
                <Grid size={ { xs: 12, lg: 8 } }>
                    <Stack spacing={ 2 }>

            { /* --- the thread ------------------------------------------------------------- */ }
            <Section title="Thread">
                { messages.length === 0 && conversation?.originalMessage == null && !loading && (
                    <Typography variant="body2" color="text.secondary">
                        { 'Nothing has been sent yet. The first reply starts the thread.' }
                    </Typography>
                ) }
                { /* A plain flex column rather than `Stack spacing`: **Stack resets its children's
                     margins**, which silently flattened the left/right indentation that tells the
                     two sides of the conversation apart (measured 2026-09-08 — both sides came back
                     at x 280, width 1059). `alignSelf` is not a margin, so it survives, and it also
                     sizes each bubble to its own content instead of the full column. */ }
                <Box
                    sx={ {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        // A recessed ground so both bubbles read as sitting on the thread rather
                        // than merging into the card behind them: the inbound one is
                        // `background.paper`, which is exactly the card's own colour.
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        p: 1.5,
                        // Capped so a long thread scrolls itself instead of pushing the composer
                        // off the bottom of the screen.
                        maxHeight: 440,
                        overflowY: 'auto',
                    } }
                >
                    { /* What they wrote on the form, which is what started all this. Rendered like
                         an inbound message but built from the request itself: it came through the
                         website rather than MailGun, so there is no `EmailMessage` for it and one
                         is deliberately not invented to make it fit. */ }
                    { conversation?.originalMessage != null && conversation.originalMessage !== '' && (
                        <Box
                            sx={ {
                                p: 1.5,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                                minWidth: '40%',
                            } }
                        >
                            <Stack direction="row" justifyContent="space-between" spacing={ 1 }>
                                <Typography variant="caption" sx={ { fontWeight: 600 } }>
                                    { `${ conversation.recipientName || 'Them' } — their enquiry` }
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    { formatMoment(conversation.originalMessageAt) }
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={ { whiteSpace: 'pre-wrap', mt: 0.5 } }>
                                { conversation.originalMessage }
                            </Typography>
                        </Box>
                    ) }

                    { messages.map((message: EmailMessage): JSX.Element => {
                        const outbound: boolean = message.direction === 'outbound';

                        return (
                            <Box
                                key={ message.id }
                                sx={ {
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: outbound ? 'primary.light' : 'divider',
                                    bgcolor: outbound ? 'action.hover' : 'background.paper',
                                    alignSelf: outbound ? 'flex-end' : 'flex-start',
                                    maxWidth: '85%',
                                    minWidth: '40%',
                                } }
                            >
                                <Stack direction="row" justifyContent="space-between" spacing={ 1 }>
                                    <Typography variant="caption" sx={ { fontWeight: 600 } }>
                                        { senderLabel(message, outbound, conversation?.recipientName ?? '') }
                                        { message.subject ? ` — ${ message.subject }` : '' }
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        { formatMoment(message.createdAt) }
                                    </Typography>
                                </Stack>
                                { /* The plain text body, never `bodyHtml`. An inbound message is
                                     written by whoever replied, so rendering its markup would put
                                     their html into the admin panel. */ }
                                <Typography variant="body2" sx={ { whiteSpace: 'pre-wrap', mt: 0.5 } }>
                                    { message.body }
                                </Typography>
                            </Box>
                        );
                    }) }
                </Box>
            </Section>



            { /* --- write a reply ---------------------------------------------------------- */ }
            <Section title="Reply">
                <Stack spacing={ 1.5 }>
                    <TextField
                        label="Subject"
                        size="small"
                        fullWidth
                        value={ replySubject }
                        onChange={ (event): void => setReplySubject(event.target.value) }
                    />
                    <TextField
                        label="Message"
                        size="small"
                        fullWidth
                        multiline
                        rows={ 10 }
                        value={ replyBody }
                        placeholder="This is sent to them. The template and signature are added automatically."
                        onChange={ (event): void => setReplyBody(event.target.value) }
                    />
                    <Box>
                        <Button
                            variant="contained"
                            disabled={ busy || replyBody.trim().length === 0 || replySubject.trim().length === 0 }
                            onClick={ (): void => {
                                void run(
                                    (): Promise<ActionResult> => sendReply(subject, replySubject, replyBody),
                                    (): void => setReplyBody('')
                                );
                            } }
                        >
                            { 'Send reply' }
                        </Button>
                    </Box>
                </Stack>
            </Section>

                    </Stack>
                </Grid>

                <Grid size={ { xs: 12, lg: 4 } }>
                    <Stack spacing={ 2 }>

            { /* --- internal notes --------------------------------------------------------- */ }
            <Section title="Notes">
                <Stack spacing={ 1.5 }>
                    <TextField
                        label="New note"
                        size="small"
                        fullWidth
                        multiline
                        rows={ 3 }
                        helperText="Internal. Never sent to them."
                        value={ noteBody }
                        onChange={ (event): void => setNoteBody(event.target.value) }
                    />
                    <Box>
                        <Button
                            variant="outlined"
                            disabled={ busy || noteBody.trim().length === 0 }
                            onClick={ (): void => {
                                void run(
                                    (): Promise<ActionResult> => addNote(subject, noteBody),
                                    (): void => setNoteBody('')
                                );
                            } }
                        >
                            { 'Add note' }
                        </Button>
                    </Box>

                    { notes.map((note): JSX.Element => (
                        <Box key={ note.id } sx={ { p: 1, borderLeft: '3px solid', borderColor: 'primary.light' } }>
                            <Typography variant="caption" color="text.secondary">
                                { `${ nameOf(note.author) ?? 'Unknown' } — ${ formatMoment(note.createdAt) }` }
                            </Typography>
                            <Typography variant="body2" sx={ { whiteSpace: 'pre-wrap' } }>
                                { note.body }
                            </Typography>
                        </Box>
                    )) }

                    { earlier.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                { 'Earlier notes (before conversations, no author recorded)' }
                            </Typography>
                            { earlier.map((entry, index: number): JSX.Element => (
                                <Box
                                    key={ `earlier-${ index }` }
                                    sx={ { p: 1, borderLeft: '3px solid', borderColor: 'divider' } }
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        { entry.date }
                                    </Typography>
                                    <Typography variant="body2" sx={ { whiteSpace: 'pre-wrap' } }>
                                        { entry.note }
                                    </Typography>
                                </Box>
                            )) }
                        </Box>
                    ) }
                </Stack>
            </Section>



            { /* --- hand it over ----------------------------------------------------------- */ }
            <Section title="Ownership">
                <Stack spacing={ 1.5 }>
                    <Typography variant="caption" color="text.secondary">
                        { 'A conversation is claimed by whoever first replies or writes a note. Owners and admins can hand it to anyone; other roles to someone on their own role.' }
                    </Typography>
                    <TextField
                        select
                        label="Hand to"
                        size="small"
                        fullWidth
                        value={ handTo }
                        onChange={ (event): void => setHandTo(event.target.value) }
                        disabled={ assigneeError !== null || assignable === null || assignable.length === 0 }
                        error={ assigneeError !== null }
                        helperText={ assigneeError ?? (assignable?.length === 0 ? 'Nobody available to hand this to.' : undefined) }
                    >
                        { (assignable ?? []).map((admin: Assignee): JSX.Element => (
                            <MenuItem key={ admin.id } value={ String(admin.id) }>
                                { admin.role === null ? admin.name : `${ admin.name } (${ admin.role })` }
                            </MenuItem>
                        )) }
                    </TextField>
                    <TextField
                        label="Reason (optional)"
                        size="small"
                        fullWidth
                        value={ handReason }
                        onChange={ (event): void => setHandReason(event.target.value) }
                    />
                    <Box>
                        <Button
                            variant="outlined"
                            disabled={ busy || handTo === '' }
                            onClick={ (): void => {
                                void run(
                                    (): Promise<ActionResult> => reassign(subject, Number(handTo), handReason),
                                    (): void => { setHandTo(''); setHandReason(''); }
                                );
                            } }
                        >
                            { 'Hand over' }
                        </Button>
                    </Box>

                    { handovers.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">{ 'History' }</Typography>
                            { handovers.map((handover): JSX.Element => (
                                <Typography
                                    key={ handover.id }
                                    variant="caption"
                                    component="div"
                                    color="text.secondary"
                                >
                                    { `${ formatMoment(handover.createdAt) } — ${ handoverLine(handover) }` }
                                </Typography>
                            )) }
                        </Box>
                    ) }
                </Stack>
            </Section>

                    </Stack>
                </Grid>
            </Grid>
        </Stack>
    );
};
